import FacialFeaturesHelper from '@/services/FacialFeaturesHelper';
import { TraitActionTypeEnum } from './../enum/trait_action_type';
import CategoryAssets from '@/services/CategoryAssets';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';
import { OutfitTypeEnum } from '@/enum/outfit_type';
import SendClientMessage from '@/services/SendClientMessage';
import { StyleEnum } from '@/enum/style';
import { GenderEnum } from '@/enum/gender';
import { FlowModeEnum } from '@/enum/flow_mode';
import { BitmojiAppSnapchatUpsellActionEnum } from '@/enum/bitmoji_app_snapchat_upsell_action';
import { BitmojiAppSnapchatUpsellTypeEnum } from '@/enum/bitmoji_app_snapchat_upsell_type';

const METRIC_NAME_KEY: string = 'event_name';
const FASHION_METRIC_OUTFIT_ACTION: string = 'bitmoji_fashion_outfit_action';
const FASHION_METRIC_CHANGE_OUTFIT: string = 'bitmoji_fashion_change_outfit';
const FASHION_METRIC_OUTFIT_KEY: string = 'bitmoji_outfit';
const FASHION_METRIC_OUTFIT_TYPE_KEY: string = 'bitmoji_outfit_type';
const FASHION_METRIC_HAS_TAPPED_MIX_AND_MATCH_ITEM_IN_SESSION_KEY: string =
  'bitmoji_has_tapped_mix_and_match_item_in_session';
const FASHION_METRIC_OUTFIT_ACTION_TYPE_KEY: string = 'bitmoji_fashion_outfit_action_type';

const TRAIT_METRIC_ACTION_TYPE_KEY: string = 'bitmoji_avatar_trait_action_type';
const TRAIT_METRIC_KEY: string = 'bitmoji_avatar_trait';
const TRAIT_METRIC_ACTION: string = 'bitmoji_avatar_trait_action';

const METRIC_CATEGORY_KEY: string = 'bitmoji_avatar_builder_trait_category';
const METRIC_GENDER_KEY: string = 'bitmoji_avatar_builder_gender';
const METRIC_FLOW_MODE_KEY: string = 'bitmoji_avatar_builder_flow_mode';

const PAGE_METRIC_VIEW: string = 'bitmoji_avatar_builder_page_view';

const BITMOJI_APP_SNAPCHAT_UPSELL: string = 'bitmoji_app_snapchat_upsell';
const BITMOJI_APP_SNAPCHAT_UPSELL_ACTION: string = 'action';
const BITMOJI_APP_SNAPCHAT_UPSELL_UPSELL_TYPE: string = 'upsell_type';

const outfitFromSelectedTraits = (traits: Record<string, number>): Record<string, number> => {
  if (traits.clothing_type === OutfitTypeEnum.SPLIT) {
    return DetailedOutfitHelper.getDetailedOutfitFromTraits(traits);
  }
  return { outfit_id: traits.outfit };
};

const logBitmojiAppSnapchatUpsell = (
  action: BitmojiAppSnapchatUpsellActionEnum,
  upsellType: BitmojiAppSnapchatUpsellTypeEnum,
): void => {
  const eventMessage: Record<string, string> = {};
  eventMessage[METRIC_NAME_KEY] = BITMOJI_APP_SNAPCHAT_UPSELL;
  eventMessage[BITMOJI_APP_SNAPCHAT_UPSELL_ACTION] = action;
  eventMessage[BITMOJI_APP_SNAPCHAT_UPSELL_UPSELL_TYPE] = upsellType;
  SendClientMessage.logEvent(eventMessage);
};

const logOutfitAction = (
  outfitActionType: string,
  selectedTraits: Record<string, number>,
  gender: GenderEnum,
  style: StyleEnum,
  selectedCategoryIndex: number,
  flowMode: FlowModeEnum,
): void => {
  const currentCategory = CategoryAssets.getFlatCategoryList(gender, style, flowMode)[
    selectedCategoryIndex
  ];
  const outfit = outfitFromSelectedTraits(selectedTraits);
  const eventMessage: Record<string, string | Record<string, number>> = {};
  eventMessage[METRIC_NAME_KEY] = FASHION_METRIC_OUTFIT_ACTION;
  eventMessage[FASHION_METRIC_OUTFIT_ACTION_TYPE_KEY] = outfitActionType;
  eventMessage[FASHION_METRIC_OUTFIT_KEY] = outfit;
  eventMessage[METRIC_FLOW_MODE_KEY] = flowMode;
  eventMessage[METRIC_CATEGORY_KEY] = currentCategory;
  SendClientMessage.logEvent(eventMessage);
};

const fromSelectedTraits = (
  traits: Record<string, number>,
  facialFeatureTraits: Record<string, string>,
  traitActionType: TraitActionTypeEnum,
  currentCategory: string,
): Record<string, number> => {
  const selectedTraits: Record<string, number> = {};
  if (
    traitActionType === TraitActionTypeEnum.TAP_PIERCING_COLOUR ||
    traitActionType === TraitActionTypeEnum.TAP_PIERCING
  ) {
    const groupId = FacialFeaturesHelper.getSelectedGroupIdForCategory(
      currentCategory,
      facialFeatureTraits,
    );
    // groupIds are not stuck to the traits record by the API definition
    if (!groupId) {
      // This should not happen if a trait has just been selected in the current category
      throw new Error(`[MetricsHelper] Could not find a selected groupId for the category`);
    }
    const relatedTraits = FacialFeaturesHelper.getCategoryTraitKeys(currentCategory, traits);
    selectedTraits['id'] = Number(groupId);
    for (const trait of relatedTraits) {
      selectedTraits[trait] = traits[trait];
    }
    return selectedTraits;
  } else if (traitActionType === TraitActionTypeEnum.TAP_TRAIT) {
    const id = traits[currentCategory]; // based on setTrait mutation
    selectedTraits['id'] = id;
    return selectedTraits;
  }

  return {};
};

const logTraitAction = (
  traitActionType: TraitActionTypeEnum,
  selectedTraits: Record<string, number>,
  facialFeatureTraits: Record<string, string>,
  gender: GenderEnum,
  style: StyleEnum,
  selectedCategoryIndex: number,
  flowMode: FlowModeEnum,
): void => {
  const currentCategory = CategoryAssets.getFlatCategoryList(gender, style, flowMode)[
    selectedCategoryIndex
  ];
  const traits = fromSelectedTraits(
    selectedTraits,
    facialFeatureTraits,
    traitActionType,
    currentCategory,
  );
  const eventMessage: Record<string, string | Record<string, number>> = {};
  eventMessage[METRIC_NAME_KEY] = TRAIT_METRIC_ACTION;
  eventMessage[TRAIT_METRIC_ACTION_TYPE_KEY] = traitActionType;
  eventMessage[TRAIT_METRIC_KEY] = traits;
  eventMessage[METRIC_FLOW_MODE_KEY] = flowMode;
  eventMessage[METRIC_CATEGORY_KEY] = currentCategory;
  SendClientMessage.logEvent(eventMessage);
};

const logOutfitChange = (
  selectedTraits: Record<string, number>,
  outfitSelectionType: string,
  hasTappedMixAndMatchItemInSession: boolean,
  flowMode: string,
): void => {
  const outfit = outfitFromSelectedTraits(selectedTraits);
  const eventMessage: Record<string, boolean | string | Record<string, number>> = {};
  eventMessage[METRIC_NAME_KEY] = FASHION_METRIC_CHANGE_OUTFIT;
  eventMessage[FASHION_METRIC_OUTFIT_TYPE_KEY] = outfitSelectionType;
  eventMessage[FASHION_METRIC_OUTFIT_KEY] = outfit;
  eventMessage[METRIC_FLOW_MODE_KEY] = flowMode;
  eventMessage[
    FASHION_METRIC_HAS_TAPPED_MIX_AND_MATCH_ITEM_IN_SESSION_KEY
  ] = hasTappedMixAndMatchItemInSession;
  SendClientMessage.logEvent(eventMessage);
};

const logPageView = (
  selectedCategoryIndex: number,
  gender: GenderEnum,
  flowMode: FlowModeEnum,
  style: StyleEnum,
): void => {
  const currentCategory = CategoryAssets.getFlatCategoryList(gender, style, flowMode)[
    selectedCategoryIndex
  ];
  const eventMessage: Record<string, string | Record<string, number>> = {};
  eventMessage[METRIC_NAME_KEY] = PAGE_METRIC_VIEW;
  eventMessage[METRIC_CATEGORY_KEY] = currentCategory;
  eventMessage[METRIC_GENDER_KEY] = gender.toString();
  eventMessage[METRIC_FLOW_MODE_KEY] = flowMode;
  SendClientMessage.logEvent(eventMessage);
};

export default {
  logBitmojiAppSnapchatUpsell,
  logOutfitAction,
  logOutfitChange,
  logTraitAction,
  logPageView,
};
